import { useEffect, useState, useMemo } from "react";
import { useParams, useLocation } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import { beneLookUp } from "../../../../redux/features/verifyPAN/verifyPAN";
import { beneSuccessDetail } from "../../../../redux/features/accounts/BeneficiaryAccount/createAccount";
import { beneBankLookUp } from "../../../../redux/features/beneficiary/beneAllVerification/beneAllVerification";
import { formatAccountNumber } from "../../../../components/_utils/FormatAccountNumber";
import StatusContainer from "../../../../components/_utils/CommonStatus/StatusContainer";
import TabsWeapperSection from "../../../../components/_utils/CommonStatus/StatusSubComponents/TabsWrapperSection";
import InfoCard from "../../../../components/_utils/CommonStatus/StatusSubComponents/StatusCardContainer/InfoCard";
import { setHideBreadCrumb } from "../../../../redux/features/hideBreadCrumb/hideBreadCrumb";
import { GlobalConfiguration } from "../../../../utils/configurationProvider";
import ICONS from "../../../../assets/Icons";
import accountNameStandardised from "../../../../utils/accountNamestandardised";

const BenePanEditSucess = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const updatedFields = location?.state?.updatedFields || [];
  const [currentAccountDetails, setCurrentAccountDetails] = useState({
    id: "",
    name: "",
    bank_name: "",
    bank_acc_no: "",
    ifsc_code: "",
    category: "",
    pan: "",
    labels: "",
  });

  const benebankLookData = useSelector(
    (state) => state.beneAllVerification.beneLookData,
  );

  useEffect(() => {
    dispatch(setHideBreadCrumb({ hide: true }));

    return () => {
      dispatch(setHideBreadCrumb({ hide: false }));
    };
  }, [dispatch]);

  useEffect(() => {
    const fetchPANSuccessDetail = async () => {
      let response = await dispatch(beneSuccessDetail(id));
      if (response?.error?.message === "Request failed with status code 404") {
        navigate("*");
      }
    };
    fetchPANSuccessDetail();
  }, [dispatch, id, navigate]);

  const successDetail = useSelector(
    (state) => state.beneCreateAccount.beneSuccessData,
  );
  const { isSuccessLoading } = useSelector((state) => state.beneCreateAccount);

  const beneficiaryAccount = useMemo(
    () => successDetail?.beneficiary_accounts?.[0] || {},
    [successDetail?.beneficiary_accounts],
  );

  useEffect(() => {
    if (
      beneficiaryAccount &&
      Object.keys(beneficiaryAccount).length >
        GlobalConfiguration.lengthCheckZero
    ) {
      const {
        id: accId,
        name,
        bank_name,
        bank_acc_no,
        ifsc_code,
        category,
        pan_no,
      } = beneficiaryAccount;
      setCurrentAccountDetails({
        id: accId,
        name,
        bank_name,
        bank_acc_no,
        ifsc_code,
        category,
        pan: pan_no,
        labels: beneficiaryAccount.labels,
      });
    }
  }, [beneficiaryAccount]);

  useEffect(() => {
    if (!isSuccessLoading && successDetail) {
      dispatch(
        beneLookUp({
          pan_number: successDetail?.beneficiary_accounts?.[0]?.pan_no,
        }),
      );
      dispatch(
        beneBankLookUp({
          ifsc: successDetail?.beneficiary_accounts?.[0]?.ifsc_code,
          bank_account_no:
            successDetail?.beneficiary_accounts?.[0]?.bank_acc_no,
        }),
      );
    }
  }, [dispatch, isSuccessLoading, successDetail]);

  const beneLookData = useSelector((state) => state.verify.beneLookData);

  const createTxnHandler = () => {
    navigate(
      "/beneficiary_transaction_dashboard/create_beneficiary_transaction",
      {
        state: currentAccountDetails,
      },
    );
  };

  const viewDetailsHandler = () => {
    navigate(
      `/beneficiary_account_dashboard/beneficiary_account_view_all/beneficiary_account_detail_page/${id}`,
    );
  };

  const dashboardHandler = () => {
    navigate("/beneficiary_account_dashboard");
  };

  const createAccountHandler = () => {
    navigate("/beneficiary_account_dashboard/create_beneficiary_account");
  };

  const fieldsToShow =
    updatedFields.length > GlobalConfiguration.lengthCheckZero
      ? updatedFields
      : ["pan_no", "gstin_detail"];

  const panGstFieldConfig = {
    pan_no: {
      leftSubTitle: "PAN number",
      getTitle: (acc) => acc?.pan_no,
    },
    gstin_detail: {
      leftSubTitle: "GSTIN (optional)",
      getTitle: (acc) => acc?.gstin_detail,
    },
  };

  const onlyGstUpdated =
    updatedFields.length === GlobalConfiguration.lengthCheckOne &&
    updatedFields[0] === "gstin_detail";
  const onlyPanUpdated =
    updatedFields.length === GlobalConfiguration.lengthCheckOne &&
    updatedFields[0] === "pan_no";
  const getPanGstSuccessTitle = () => {
    if (onlyGstUpdated) return "GST number updated successfully.";
    if (onlyPanUpdated) return "PAN number updated successfully.";
    return "PAN and GST number updated successfully.";
  };

  const renderPanGstCards = () => {
    return fieldsToShow
      .filter((key) => panGstFieldConfig[key])
      .filter(
        (key) => key !== "gstin_detail" || beneficiaryAccount?.gstin_detail,
      )
      .map((key) => {
        const config = panGstFieldConfig[key];
        const title = config.getTitle(beneficiaryAccount);
        if (key === "gstin_detail" && !title) return null;
        return (
          <InfoCard
            key={key}
            bgColor="var(--positive-card)"
            rows={[
              {
                leftTitle: title || "Not Given",
                leftSubTitle: config.leftSubTitle,
              },
            ]}
          />
        );
      });
  };

  const buttonRows = [
    [
      {
        btnText: "Create account",
        enabledBgColor: "var(--text-primary)",
        enabledTextColor: "var(--positive-bg)",
        icon: "https://q2pay.in/new_assets/common/create_flow/create_new_account.svg",
        handleFunction: createAccountHandler,
        iconToBeAlign: "left",
        fullWidthRequired: true,
        imgColor: "var(--positive-bg)",
      },
      {
        btnText: "Create transaction",
        enabledBgColor: "var(--text-primary)",
        enabledTextColor: "var(--positive-bg)",
        icon: ICONS.ADD_BUTTON,
        handleFunction: createTxnHandler,
        iconToBeAlign: "left",
        fullWidthRequired: true,
        imgColor: "var(--positive-bg)",
      },
      {
        btnText: "View details",
        enabledBgColor: "var(--text-primary)",
        enabledTextColor: "var(--positive-bg)",
        icon: ICONS.VIEW_DETAILS,
        handleFunction: viewDetailsHandler,
        iconToBeAlign: "left",
        fullWidthRequired: true,
        imgColor: "var(--positive-bg)",
      },
    ],
    [
      {
        btnText: "Go to dashboard",
        enabledBgColor: "var(--bg-positive-bg)",
        enabledTextColor: "var(--text-primary)",
        icon: ICONS.DASHBOARD,
        handleFunction: dashboardHandler,
        iconToBeAlign: "left",
        fullWidthRequired: true,
        outLineColor: "var(--text-primary)",
      },
    ],
  ];

  return (
    <div>
      <StatusContainer
        isLoading={isSuccessLoading}
        status="success"
        buttonRows={buttonRows}
        title={getPanGstSuccessTitle()}
      >
        <TabsWeapperSection title="Account details">
          <>{renderPanGstCards()}</>
        </TabsWeapperSection>
        {beneLookData?.beneficiary_accounts?.length >
          GlobalConfiguration.lengthCheckOne && (
          <TabsWeapperSection title="Accounts Associated to PAN">
            {beneLookData?.beneficiary_accounts
              ?.filter(
                (accountData) =>
                  accountData?.approval_status === "pending" ||
                  accountData?.approval_status === "approved",
              )
              .map((accountData, id) => (
                <InfoCard
                  key={id}
                  bgColor="var(--positive-card)"
                  header={true}
                  borderColor="var(--positive-bg)"
                  rightHeaderText={`${
                    accountData?.approval_status?.charAt(0).toUpperCase() +
                    accountData?.approval_status?.slice(1)
                  } account`}
                  rightHeaderTextColor="var(--text-secondary)"
                  leftHeaderText={accountData?.bank_account_holder_full_name}
                  leftHeaderWidth="w-width-lg"
                  rows={[
                    {
                      leftTitle: accountData?.bank_acc_no,
                      leftSubTitle: "Bank account number",
                      ifsc: accountData?.ifsc_code,
                    },
                  ]}
                />
              ))}
          </TabsWeapperSection>
        )}
        {benebankLookData?.beneficiary_accounts?.length >
          GlobalConfiguration.lengthCheckOne && (
          <TabsWeapperSection title="Accounts Associated to Bank">
            {benebankLookData?.beneficiary_accounts
              .filter(
                (accountData) =>
                  accountData?.approval_status === "pending" ||
                  accountData?.approval_status === "approved",
              )
              .map((accountData, id) => (
                <InfoCard
                  key={id}
                  bgColor="var(--positive-card)"
                  header={true}
                  borderColor="var(--positive-bg)"
                  rightHeaderText={`${
                    accountData?.approval_status?.charAt(0).toUpperCase() +
                    accountData?.approval_status?.slice(1)
                  } account`}
                  rightHeaderTextColor="var(--text-secondary)"
                  leftHeaderText={accountData?.name}
                  leftHeaderWidth="w-width-lg"
                  rows={[
                    {
                      leftTitle: accountData?.pan_no,
                      leftSubTitle: "PAN number",
                    },
                  ]}
                />
              ))}
          </TabsWeapperSection>
        )}
      </StatusContainer>
    </div>
  );
};

export default BenePanEditSucess;
